import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "./backendProbe.js";
import "./HomePage.css";
import "./Resources.css";
import PageHeader from "./components/PageHeader.js";
import SiteFooter from "./components/SiteFooter.js";

const LS_TEMPLATES_KEY = "zolaid_templates_v1";
const LS_TEMPLATE_TO_LOAD = "zolaid_template_to_load_v1";
const LS_TEMPLATE_EDIT_KEYS = "zolaid_template_edit_keys_v1";

function getEditKeyMap() {
  try {
    const raw = localStorage.getItem(LS_TEMPLATE_EDIT_KEYS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function setEditKeyForTemplateId(id, editKey) {
  try {
    const map = getEditKeyMap();
    map[String(id)] = String(editKey);
    localStorage.setItem(LS_TEMPLATE_EDIT_KEYS, JSON.stringify(map));
  } catch (_) {}
}

function getEditKeyForTemplateId(id) {
  try {
    const map = getEditKeyMap();
    return map && id ? String(map[String(id)] || "") : "";
  } catch (_) {
    return "";
  }
}

function copyText(text, label = "Copied.") {
  try {
    const t = String(text || "");
    if (!t) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(t).then(() => alert(label)).catch(() => window.prompt("Copy:", t));
    } else {
      window.prompt("Copy:", t);
    }
  } catch (_) {}
}

function loadTemplates() {
  try {
    const raw = localStorage.getItem(LS_TEMPLATES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function saveTemplates(templates) {
  localStorage.setItem(LS_TEMPLATES_KEY, JSON.stringify(Array.isArray(templates) ? templates : []));
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Templates() {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState(() => loadTemplates());
  const [importText, setImportText] = useState("");
  const [community, setCommunity] = useState([]);
  const [communitySort, setCommunitySort] = useState("createdAt"); // createdAt | views | likes | hot
  const [communityOffset, setCommunityOffset] = useState(0);
  const [communityHasMore, setCommunityHasMore] = useState(false);
  const [shareId, setShareId] = useState("");
  const [communitySearch, setCommunitySearch] = useState("");
  const [communitySearchDebounced, setCommunitySearchDebounced] = useState("");
  const [recommendQuery, setRecommendQuery] = useState("");
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [recommendError, setRecommendError] = useState("");
  const [recommendations, setRecommendations] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState("");
  const [selectedCommunityLoading, setSelectedCommunityLoading] = useState(false);
  const [selectedCommunityError, setSelectedCommunityError] = useState("");
  const [selectedCommunityTemplate, setSelectedCommunityTemplate] = useState(null); // { id, name, description, tags, websiteData?, comments? }
  const [ownerVersions, setOwnerVersions] = useState([]);
  const [ownerVersionsLoading, setOwnerVersionsLoading] = useState(false);
  const [ownerVersionsError, setOwnerVersionsError] = useState("");
  const [claimId, setClaimId] = useState("");
  const [claimEditKey, setClaimEditKey] = useState("");
  const [sourceMetaById, setSourceMetaById] = useState({}); // { [id]: { id, name } }
  const [newCommentText, setNewCommentText] = useState("");
  const [communityError, setCommunityError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    saveTemplates(templates);
  }, [templates]);

  const sorted = useMemo(() => {
    return [...templates].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [templates]);

  const doImport = () => {
    try {
      setError("");
      const parsed = JSON.parse(importText || "{}");
      if (!parsed || typeof parsed !== "object") throw new Error("Invalid JSON");
      const item = {
        id: `tpl_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: String(parsed.name || "Imported template"),
        createdAt: Date.now(),
        websiteData: parsed.websiteData && typeof parsed.websiteData === "object" ? parsed.websiteData : parsed,
      };
      setTemplates((prev) => [item, ...prev].slice(0, 50));
      setImportText("");
    } catch (e) {
      setError(e?.message || "Failed to import template.");
    }
  };

  const doLoadIntoBuilder = (tpl) => {
    try {
      localStorage.setItem(LS_TEMPLATE_TO_LOAD, JSON.stringify(tpl));
      // WebsiteBuilder lives inside /chatbot modal, so we navigate there.
      navigate("/chatbot");
    } catch (_) {}
  };

  const apiUrl = getApiBase();

  const makeTemplateShareLink = (id) => {
    try {
      const origin = window.location.origin;
      return `${origin}/Templates?view=${encodeURIComponent(String(id || ""))}`;
    } catch (_) {
      return `/Templates?view=${encodeURIComponent(String(id || ""))}`;
    }
  };

  const copyTemplateShareLink = async (id) => {
    try {
      const link = makeTemplateShareLink(id);
      await navigator.clipboard.writeText(String(link));
      alert("Share link copied.");
    } catch (_) {
      try {
        const link = makeTemplateShareLink(id);
        window.prompt("Copy this link:", link);
      } catch (__e) {}
    }
  };

  const setViewInUrl = (id) => {
    try {
      const params = new URLSearchParams(String(location?.search || ""));
      if (id) params.set("view", String(id));
      else params.delete("view");
      const qs = params.toString();
      navigate({ pathname: "/Templates", search: qs ? `?${qs}` : "" }, { replace: false });
    } catch (_) {}
  };

  const refreshCommunity = async ({ append = false, q = communitySearchDebounced } = {}) => {
    try {
      setCommunityError("");
      const endpoint = apiUrl ? `${apiUrl}/market/templates/list` : "/market/templates/list";
      const nextOffset = append ? communityOffset : 0;
      const resp = await axios.get(endpoint, { params: { sort: communitySort, limit: 50, offset: nextOffset, q: String(q || "").trim() } });
      const items = Array.isArray(resp.data?.templates) ? resp.data.templates : [];
      const total = Number(resp.data?.total || 0);
      const offset = Number(resp.data?.offset || nextOffset);
      const limit = Number(resp.data?.limit || 50);
      const merged = append ? [...community, ...items] : items;
      setCommunity(merged);
      setCommunityOffset(offset + limit);
      setCommunityHasMore(total ? (offset + limit) < total : (items.length === limit));
    } catch (e) {
      console.error("community list failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to load community templates.");
    }
  };

  const fetchTemplateMeta = async (id) => {
    const tid = String(id || "").trim();
    if (!tid) return null;
    try {
      const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
      const resp = await axios.get(endpoint, { params: { id: tid } });
      const t = resp.data?.template;
      const meta = t && typeof t === "object" ? { id: tid, name: String(t.name || tid) } : null;
      if (meta) {
        setSourceMetaById((prev) => ({ ...(prev || {}), [tid]: meta }));
      }
      return meta;
    } catch (_) {
      return null;
    }
  };

  const recommendTemplates = async () => {
    try {
      setRecommendError("");
      setRecommendLoading(true);
      const q = String(recommendQuery || "").trim();
      if (!q) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/recommend` : "/market/templates/recommend";
      const resp = await axios.post(endpoint, { query: q });
      setRecommendations(Array.isArray(resp.data?.recommendations) ? resp.data.recommendations : []);
    } catch (e) {
      console.error("recommend failed", e);
      setRecommendError(e?.response?.data?.error || "Failed to recommend templates.");
      setRecommendations([]);
    } finally {
      setRecommendLoading(false);
    }
  };

  const likeCommunity = async (id) => {
    try {
      setCommunityError("");
      const endpoint = apiUrl ? `${apiUrl}/market/templates/like` : "/market/templates/like";
      await axios.post(endpoint, { id });
      await refreshCommunity();
    } catch (e) {
      console.error("like failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to like template.");
    }
  };

  const remixCommunity = async (id) => {
    try {
      setCommunityError("");
      const endpoint = apiUrl ? `${apiUrl}/market/templates/remix` : "/market/templates/remix";
      const name = prompt("Remix name (optional):") || "";
      const description = prompt("Description (optional):") || "";
      const tagsRaw = prompt("Tags (optional, comma-separated):") || "";
      const author =
        prompt(
          "Author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", author); } catch (_) {}
      const tags = tagsRaw.split(",").map((t) => String(t).trim()).filter(Boolean).slice(0, 10);

      const payload = { id };
      if (name.trim()) payload.name = name.trim();
      if (description.trim()) payload.description = description.trim();
      if (tags.length) payload.tags = tags;
      if (author.trim()) payload.author = author.trim();

      const resp = await axios.post(endpoint, payload);
      const newId = resp.data?.id;
      const editKey = resp.data?.editKey;
      if (newId && editKey) setEditKeyForTemplateId(newId, editKey);
      if (newId && editKey) copyText(String(editKey), "editKey copied (save this to manage the remix on other devices).");
      if (newId) setShareId(String(newId));
      await refreshCommunity();
    } catch (e) {
      console.error("remix failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to remix template.");
    }
  };

  const reportCommunity = async (id) => {
    try {
      setCommunityError("");
      const reason = prompt("Report reason (spam, offensive, copyrighted, etc):");
      if (!reason) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/report` : "/market/templates/report";
      await axios.post(endpoint, { id, reason });
      await refreshCommunity();
    } catch (e) {
      console.error("report failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to report template.");
    }
  };

  const commentCommunity = async (id) => {
    try {
      setCommunityError("");
      const text = prompt("Comment (visible to community):");
      if (!text) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/comment` : "/market/templates/comment";
      await axios.post(endpoint, { id, text });
      await refreshCommunity();
    } catch (e) {
      console.error("comment failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to add comment.");
    }
  };

  const loadCommunityTemplate = async (id) => {
    try {
      setSelectedCommunityError("");
      setSelectedCommunityLoading(true);
      setSelectedCommunityId(String(id || ""));
      // best-effort view tracking
      try {
        const tid = String(id || "").trim();
        const key = `zolaid_tpl_viewed_${tid}`;
        let shouldCount = true;
        try {
          if (sessionStorage.getItem(key)) shouldCount = false;
          else sessionStorage.setItem(key, "1");
        } catch (_) {}
        if (shouldCount) {
          const viewEndpoint = apiUrl ? `${apiUrl}/market/templates/view` : "/market/templates/view";
          axios.post(viewEndpoint, { id: tid }).catch(() => {});
        }
      } catch (_) {}
      const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
      const resp = await axios.get(endpoint, { params: { id } });
      const tpl = resp.data?.template || null;
      setSelectedCommunityTemplate(tpl);
      const srcId = tpl?.sourceId ? String(tpl.sourceId) : "";
      const srcObj = tpl?.source && typeof tpl.source === "object" ? tpl.source : null;
      if (srcObj?.id && srcObj?.name) {
        setSourceMetaById((prev) => ({ ...(prev || {}), [String(srcObj.id)]: { id: String(srcObj.id), name: String(srcObj.name) } }));
      } else if (srcId && !sourceMetaById[srcId]) {
        fetchTemplateMeta(srcId);
      }
    } catch (e) {
      console.error("load template failed", e);
      setSelectedCommunityError(e?.response?.data?.error || "Failed to load template.");
      setSelectedCommunityTemplate(null);
    } finally {
      setSelectedCommunityLoading(false);
    }
  };

  const addCommentToSelected = async () => {
    try {
      const id = String(selectedCommunityId || "").trim();
      const text = String(newCommentText || "").trim();
      if (!id || !text) return;
      setSelectedCommunityError("");
      const endpoint = apiUrl ? `${apiUrl}/market/templates/comment` : "/market/templates/comment";
      await axios.post(endpoint, { id, text });
      setNewCommentText("");
      await loadCommunityTemplate(id);
      await refreshCommunity();
    } catch (e) {
      console.error("add comment failed", e);
      setSelectedCommunityError(e?.response?.data?.error || "Failed to add comment.");
    }
  };

  const createProjectFromSelectedTemplate = async ({ openAfter = false } = {}) => {
    try {
      const tpl = selectedCommunityTemplate;
      const id = String(selectedCommunityId || "").trim();
      if (!tpl || !id) return;
      const websiteData = tpl.websiteData && typeof tpl.websiteData === "object" ? tpl.websiteData : null;
      if (!websiteData) {
        setSelectedCommunityError("This template has no websiteData attached.");
        return;
      }

      const apiUrl = getApiBase();
      const endpointCreate = apiUrl ? `${apiUrl}/projects/create` : "/projects/create";
      const resp = await axios.post(endpointCreate, {
        name: `${String(tpl.name || "Template")} (Project)`,
        websiteData,
      });
      const projectId = resp.data?.project?.id;
      if (!projectId) throw new Error("Project id missing.");

      try { localStorage.setItem("zolaid_current_project_id_v1", String(projectId)); } catch (_) {}

      if (openAfter) {
        // Fetch full project then handoff to WebsiteBuilder
        const endpointGet = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
        const pr = await axios.get(endpointGet, { params: { id: projectId } });
        const project = pr.data?.project;
        if (project) {
          try { localStorage.setItem("zolaid_project_to_load_v1", JSON.stringify(project)); } catch (_) {}
          window.location.assign("/chatbot");
        }
      } else {
        alert(`Project created: ${projectId}`);
      }
    } catch (e) {
      console.error("create project from template failed", e);
      setSelectedCommunityError(e?.response?.data?.error || e?.message || "Failed to create project.");
    }
  };

  const publishTemplate = async (tpl) => {
    try {
      setCommunityError("");
      const endpoint = apiUrl ? `${apiUrl}/market/templates/publish` : "/market/templates/publish";
      const description = prompt("Template description (optional):") || "";
      const tagsRaw = prompt("Tags (optional, comma-separated, lowercase like: saas,landing,agency):") || "";
      const author =
        prompt(
          "Author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", author); } catch (_) {}
      const tags = tagsRaw
        .split(",")
        .map((t) => String(t).trim())
        .filter(Boolean)
        .slice(0, 10);
      const resp = await axios.post(endpoint, { template: { name: tpl.name, description, tags, author, websiteData: tpl.websiteData } });
      const id = resp.data?.id;
      const editKey = resp.data?.editKey;
      if (id && editKey) setEditKeyForTemplateId(id, editKey);
      if (id && editKey) copyText(String(editKey), "editKey copied (save this to manage the template on other devices).");
      if (id) {
        setShareId(String(id));
        await refreshCommunity();
      }
    } catch (e) {
      console.error("publish failed", e);
      setCommunityError(e?.response?.data?.error || "Failed to publish template.");
    }
  };

  const updatePublishedTemplate = async (templateId) => {
    try {
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        alert("No edit key found for this template on this browser.");
        return;
      }
      const endpointGet = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
      const current = await axios.get(endpointGet, { params: { id: templateId } });
      const tpl = current.data?.template;
      if (!tpl || typeof tpl !== "object") throw new Error("Template not found.");

      const nextName = prompt("Update template name:", String(tpl.name || "Template")) || String(tpl.name || "Template");
      const nextDescription = prompt("Update description (optional):", String(tpl.description || "")) || "";
      const tagsRaw = prompt("Update tags (comma-separated):", Array.isArray(tpl.tags) ? tpl.tags.join(",") : "") || "";
      const nextAuthor =
        prompt(
          "Update author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", nextAuthor); } catch (_) {}

      const nextTags = tagsRaw
        .split(",")
        .map((t) => String(t).trim())
        .filter(Boolean)
        .slice(0, 10);

      const endpointUpdate = apiUrl ? `${apiUrl}/market/templates/update` : "/market/templates/update";
      await axios.post(endpointUpdate, {
        id: templateId,
        editKey,
        patch: {
          name: nextName,
          description: nextDescription,
          tags: nextTags,
          author: nextAuthor,
          websiteData: tpl.websiteData,
        },
      });
      alert("Template updated.");
      await refreshCommunity();
      await loadCommunityTemplate(templateId);
    } catch (e) {
      console.error("update template failed", e);
      alert(e?.response?.data?.error || e?.message || "Failed to update template.");
    }
  };

  const deletePublishedTemplate = async (templateId) => {
    try {
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        alert("No edit key found for this template on this browser.");
        return;
      }
      const ok = window.confirm("Delete this published template? This cannot be undone.");
      if (!ok) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/delete-own` : "/market/templates/delete-own";
      await axios.post(endpoint, { id: templateId, editKey });
      alert("Template deleted.");
      if (selectedCommunityTemplate && String(selectedCommunityTemplate.id) === String(templateId)) {
        setSelectedCommunityTemplate(null);
      }
      setSelectedCommunityId("");
      await refreshCommunity();
    } catch (e) {
      console.error("delete template failed", e);
      alert(e?.response?.data?.error || "Failed to delete template.");
    }
  };

  const openCommunityTemplateInBuilder = async (templateId) => {
    try {
      setSelectedCommunityError("");
      const id = String(templateId || "").trim();
      if (!id) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
      const resp = await axios.get(endpoint, { params: { id } });
      const tpl = resp.data?.template;
      if (!tpl || typeof tpl !== "object") throw new Error("Template not found.");
      if (!tpl.websiteData || typeof tpl.websiteData !== "object") throw new Error("Template has no websiteData.");
      // Reuse the existing Template -> WebsiteBuilder handoff
      localStorage.setItem(LS_TEMPLATE_TO_LOAD, JSON.stringify(tpl));
      window.location.assign("/chatbot");
    } catch (e) {
      console.error("open in builder failed", e);
      setSelectedCommunityError(e?.response?.data?.error || e?.message || "Failed to open in builder.");
    }
  };

  const saveCommunityTemplateToLocal = async (templateId) => {
    try {
      const id = String(templateId || "").trim();
      if (!id) return;

      const tpl =
        selectedCommunityTemplate && String(selectedCommunityTemplate.id) === id
          ? selectedCommunityTemplate
          : null;

      let resolved = tpl;
      if (!resolved) {
        const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
        const resp = await axios.get(endpoint, { params: { id } });
        resolved = resp.data?.template;
      }

      if (!resolved || typeof resolved !== "object") throw new Error("Template not found.");
      if (!resolved.websiteData || typeof resolved.websiteData !== "object") throw new Error("Template has no websiteData.");

      const item = {
        id: `tpl_local_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: String(resolved.name || "Community template"),
        createdAt: Date.now(),
        websiteData: resolved.websiteData,
        description: String(resolved.description || ""),
        tags: Array.isArray(resolved.tags) ? resolved.tags : [],
      };

      setTemplates((prev) => [item, ...prev].slice(0, 50));
      alert("Saved to local templates.");
    } catch (e) {
      console.error("save to local failed", e);
      alert(e?.response?.data?.error || e?.message || "Failed to save to local.");
    }
  };

  const downloadCommunityTemplateJson = async (templateId) => {
    try {
      const id = String(templateId || "").trim();
      if (!id) return;

      const tpl =
        selectedCommunityTemplate && String(selectedCommunityTemplate.id) === id
          ? selectedCommunityTemplate
          : null;

      let resolved = tpl;
      if (!resolved) {
        const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
        const resp = await axios.get(endpoint, { params: { id } });
        resolved = resp.data?.template;
      }

      if (!resolved || typeof resolved !== "object") throw new Error("Template not found.");
      const safeName = String(resolved.name || "template").replace(/[^\w-]+/g, "_").slice(0, 60) || "template";
      downloadJson(`zolaid-community-template-${safeName}-${id}.json`, resolved);
    } catch (e) {
      console.error("download community json failed", e);
      alert(e?.response?.data?.error || e?.message || "Failed to download template JSON.");
    }
  };

  const refreshOwnerVersions = async (templateId) => {
    try {
      setOwnerVersionsError("");
      setOwnerVersionsLoading(true);
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        setOwnerVersions([]);
        return;
      }
      const endpoint = apiUrl ? `${apiUrl}/market/templates/versions` : "/market/templates/versions";
      const resp = await axios.post(endpoint, { id: templateId, editKey });
      setOwnerVersions(Array.isArray(resp.data?.versions) ? resp.data.versions : []);
    } catch (e) {
      console.error("owner versions failed", e);
      setOwnerVersionsError(e?.response?.data?.error || "Failed to load versions.");
      setOwnerVersions([]);
    } finally {
      setOwnerVersionsLoading(false);
    }
  };

  const restoreOwnerVersion = async (templateId, idx) => {
    try {
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        alert("No edit key found for this template on this browser.");
        return;
      }
      const ok = window.confirm("Restore this version? Current published state will be snapshotted so you can undo.");
      if (!ok) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/restore-version` : "/market/templates/restore-version";
      await axios.post(endpoint, { id: templateId, editKey, idx });
      alert("Version restored.");
      await refreshCommunity();
      await loadCommunityTemplate(templateId);
      await refreshOwnerVersions(templateId);
    } catch (e) {
      console.error("restore version failed", e);
      alert(e?.response?.data?.error || "Failed to restore version.");
    }
  };

  const importFromShareId = async () => {
    try {
      setCommunityError("");
      const id = String(shareId || "").trim();
      if (!id) return;
      const endpoint = apiUrl ? `${apiUrl}/market/templates/get` : "/market/templates/get";
      const resp = await axios.get(endpoint, { params: { id } });
      const tpl = resp.data?.template;
      if (!tpl || typeof tpl !== "object") throw new Error("Template not found");
      const item = {
        id: `tpl_local_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: String(tpl.name || "Community template"),
        createdAt: Date.now(),
        websiteData: tpl.websiteData && typeof tpl.websiteData === "object" ? tpl.websiteData : tpl,
        description: String(tpl.description || ""),
        tags: Array.isArray(tpl.tags) ? tpl.tags : [],
      };
      setTemplates((prev) => [item, ...prev].slice(0, 50));
    } catch (e) {
      console.error("import share failed", e);
      setCommunityError(e?.response?.data?.error || e?.message || "Failed to import template.");
    }
  };

  useEffect(() => {
    refreshCommunity({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // when sort changes, restart pagination
    setCommunityOffset(0);
    refreshCommunity({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communitySort]);

  useEffect(() => {
    // when search changes, restart pagination (server-side q)
    const t = setTimeout(() => setCommunitySearchDebounced(String(communitySearch || "")), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communitySearch]);

  useEffect(() => {
    setCommunityOffset(0);
    refreshCommunity({ append: false, q: communitySearchDebounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communitySearchDebounced]);

  // Deep-link: /Templates?view=<templateId>
  useEffect(() => {
    try {
      const params = new URLSearchParams(String(location?.search || ""));
      const id = String(params.get("view") || "").trim();
      if (!id) return;
      setSelectedCommunityId(id);
      loadCommunityTemplate(id);
      refreshOwnerVersions(id);
    } catch (_) {}
    // only when URL search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search]);

  const filteredCommunity = useMemo(() => {
    // server-side filtered now; keep as-is (local fallback)
    return community;
  }, [community]);

  const ownedCommunity = useMemo(() => {
    return (filteredCommunity || []).filter((c) => Boolean(getEditKeyForTemplateId(c?.id)));
  }, [filteredCommunity]);

  const goToTemplateId = async (id) => {
    const tid = String(id || "").trim();
    if (!tid) return;
    setSelectedCommunityId(tid);
    setViewInUrl(tid);
    await loadCommunityTemplate(tid);
  };

  return (
    <div className="resources-page flex flex-col min-h-screen">
      <PageHeader extraNav={[{ label: "Roadmap", to: "/Roadmap" }, { label: "Templates", to: "/Templates" }]} />
      <main id="main-content" className="resources-content flex-1">
        <h1>Templates</h1>
        <p className="resources-subtitle">
          Save, export, import, and load website templates. Browse community templates or use your own in the Website Builder.
        </p>

        <section style={sectionStyle} aria-labelledby="import-heading">
          <h2 id="import-heading">Import template JSON</h2>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={6}
            placeholder='Paste template JSON here (export format or raw websiteData)'
            style={textareaStyle}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button className="generate-btn" type="button" onClick={doImport} disabled={!importText.trim()}>
              Import
            </button>
            <button className="generate-btn" type="button" onClick={() => setImportText("")}>
              Clear
            </button>
          </div>
          {error && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{error}</div>}
        </section>

        <section style={sectionStyle} aria-labelledby="community-heading">
          <h2 id="community-heading">Community templates</h2>

          <div style={{ marginTop: 10, padding: "12px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
            <div style={{ fontWeight: 900 }}>Claim ownership on this browser (Prototype)</div>
            <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
              If you published from another device, paste the template <b>Share ID</b> and <b>editKey</b> to enable owner actions here.
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={claimId}
                onChange={(e) => setClaimId(e.target.value)}
                placeholder="Share ID (tpl_...)"
                style={{
                  flex: "1 1 220px",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(0,0,0,0.18)",
                  color: "#ffffff",
                  outline: "none",
                }}
              />
              <input
                value={claimEditKey}
                onChange={(e) => setClaimEditKey(e.target.value)}
                placeholder="editKey (edit_...)"
                style={{
                  flex: "1 1 260px",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(0,0,0,0.18)",
                  color: "#ffffff",
                  outline: "none",
                }}
              />
              <button
                className="generate-btn"
                type="button"
                disabled={!String(claimId || "").trim() || !String(claimEditKey || "").trim()}
                onClick={() => {
                  const id = String(claimId || "").trim();
                  const k = String(claimEditKey || "").trim();
                  if (!id || !k) return;
                  setEditKeyForTemplateId(id, k);
                  setClaimId("");
                  setClaimEditKey("");
                  alert("Edit key saved on this browser.");
                  refreshCommunity();
                }}
              >
                Save edit key
              </button>
            </div>
          </div>

          {ownedCommunity.length > 0 ? (
            <div style={{ marginTop: 12, padding: "12px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
              <div style={{ fontWeight: 900 }}>Owned templates (on this browser)</div>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                {ownedCommunity.slice(0, 6).map((c) => (
                  <div key={`owned_${c.id}`} style={cardStyle}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span>{c.name || "Template"}</span>
                        <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, border: "1px solid rgba(34,197,94,0.45)", color: "rgba(34,197,94,0.95)", background: "rgba(34,197,94,0.12)" }}>
                          Owned
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                        {c.id} â€¢ {c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button className="generate-btn" type="button" onClick={() => { setSelectedCommunityId(c.id); setViewInUrl(c.id); loadCommunityTemplate(c.id); refreshOwnerVersions(c.id); }}>
                        Manage
                      </button>
                      <button className="generate-btn" type="button" onClick={() => copyTemplateShareLink(c.id)}>
                        Copy link
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <p style={{ color: "rgba(255,255,255,0.75)" }}>
            Publish a template to the backend (in-memory) and import by Share ID.
          </p>
          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Recommend templates (Prototype)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={recommendQuery}
                onChange={(e) => setRecommendQuery(e.target.value)}
                placeholder="What are you building? (e.g., SaaS landing page for accountants)"
                style={{
                  flex: "1 1 320px",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(0,0,0,0.18)",
                  color: "#ffffff",
                  outline: "none",
                }}
              />
              <button className="generate-btn" type="button" disabled={recommendLoading || !recommendQuery.trim()} onClick={recommendTemplates}>
                {recommendLoading ? "Recommending..." : "Recommend"}
              </button>
            </div>
            {recommendError && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{recommendError}</div>}
            {Array.isArray(recommendations) && recommendations.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {recommendations.slice(0, 8).map((r) => (
                  <div key={`rec_${r.id}`} style={{ padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                    <div style={{ fontWeight: 900 }}>{String(r.id || "")}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", marginTop: 4 }}>{String(r.reason || "")}</div>
                    <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="generate-btn" type="button" onClick={() => { setShareId(String(r.id || "")); importFromShareId(); }}>
                        Import
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
            <input
              value={communitySearch}
              onChange={(e) => setCommunitySearch(e.target.value)}
              placeholder="Search community (name, tags, description, id)..."
              style={{
                flex: "1 1 280px",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              value={shareId}
              onChange={(e) => setShareId(e.target.value)}
              placeholder="Share ID (e.g., tpl_...)"
              style={{
                flex: "1 1 280px",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            />
            <button className="generate-btn" type="button" onClick={importFromShareId} disabled={!shareId.trim()}>
              Import
            </button>
            <select
              value={communitySort}
              onChange={(e) => setCommunitySort(e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            >
              <option value="createdAt">Sort: New</option>
              <option value="hot">Sort: Hot</option>
              <option value="views">Sort: Views</option>
              <option value="likes">Sort: Likes</option>
            </select>
            <button className="generate-btn" type="button" onClick={() => refreshCommunity({ append: false })}>
              Refresh list
            </button>
            {communityHasMore ? (
              <button className="generate-btn" type="button" onClick={() => refreshCommunity({ append: true })}>
                Load more
              </button>
            ) : null}
          </div>
          {communityError && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{communityError}</div>}

          {selectedCommunityId && (
            <div style={{ marginTop: 12, padding: "12px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 900 }}>Template details</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                    {selectedCommunityId}
                    {selectedCommunityTemplate?.viewCount != null ? ` â€¢ ðŸ‘ï¸ ${Number(selectedCommunityTemplate.viewCount || 0)}` : ""}
                    {selectedCommunityTemplate?.updatedAt ? ` â€¢ Updated ${new Date(Number(selectedCommunityTemplate.updatedAt)).toLocaleString()}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="generate-btn" type="button" disabled={selectedCommunityLoading} onClick={() => loadCommunityTemplate(selectedCommunityId)}>
                    {selectedCommunityLoading ? "Loading..." : "Refresh"}
                  </button>
                  <button className="generate-btn" type="button" disabled={!selectedCommunityTemplate?.websiteData} onClick={() => openCommunityTemplateInBuilder(selectedCommunityId)}>
                    Open in Builder
                  </button>
                  <button className="generate-btn" type="button" onClick={() => saveCommunityTemplateToLocal(selectedCommunityId)}>
                    Save to Local
                  </button>
                  <button className="generate-btn" type="button" onClick={() => downloadCommunityTemplateJson(selectedCommunityId)}>
                    Download JSON
                  </button>
                  <button className="generate-btn" type="button" onClick={() => copyTemplateShareLink(selectedCommunityId)}>
                    Copy share link
                  </button>
                  {getEditKeyForTemplateId(selectedCommunityId) ? (
                    <button className="generate-btn" type="button" onClick={() => copyText(getEditKeyForTemplateId(selectedCommunityId), "editKey copied.")}>
                      Copy editKey
                    </button>
                  ) : null}
                  {getEditKeyForTemplateId(selectedCommunityId) ? (
                    <>
                      <button className="generate-btn" type="button" onClick={() => updatePublishedTemplate(selectedCommunityId)}>
                        Update (owner)
                      </button>
                      <button className="cancel-btn" type="button" onClick={() => deletePublishedTemplate(selectedCommunityId)}>
                        Delete (owner)
                      </button>
                      <button className="generate-btn" type="button" disabled={ownerVersionsLoading} onClick={() => refreshOwnerVersions(selectedCommunityId)}>
                        {ownerVersionsLoading ? "Loading versions..." : "Versions"}
                      </button>
                    </>
                  ) : null}
                  <button className="generate-btn" type="button" disabled={!selectedCommunityTemplate?.websiteData} onClick={() => createProjectFromSelectedTemplate({ openAfter: false })}>
                    Create project
                  </button>
                  <button className="generate-btn" type="button" disabled={!selectedCommunityTemplate?.websiteData} onClick={() => createProjectFromSelectedTemplate({ openAfter: true })}>
                    Create + open
                  </button>
                  <button className="cancel-btn" type="button" onClick={() => { setSelectedCommunityId(""); setSelectedCommunityTemplate(null); setSelectedCommunityError(""); setNewCommentText(""); setOwnerVersions([]); setOwnerVersionsError(""); setViewInUrl(""); }}>
                    Close
                  </button>
                </div>
              </div>
              {selectedCommunityError && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{selectedCommunityError}</div>}
              {selectedCommunityTemplate && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontWeight: 900 }}>{selectedCommunityTemplate.name || "Template"}</div>
                  {selectedCommunityTemplate.author ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                      by {String(selectedCommunityTemplate.author)}
                    </div>
                  ) : null}
                  {selectedCommunityTemplate.sourceId ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                      Remix of{" "}
                      <button
                        type="button"
                        className="generate-btn"
                        style={{ padding: "2px 8px", fontSize: 12 }}
                        onClick={() => goToTemplateId(selectedCommunityTemplate.sourceId)}
                      >
                        {String(selectedCommunityTemplate.source?.name || sourceMetaById?.[String(selectedCommunityTemplate.sourceId)]?.name || selectedCommunityTemplate.sourceId)}
                        {" "}
                        <span style={{ opacity: 0.7 }}>({String(selectedCommunityTemplate.sourceId)})</span>
                      </button>
                    </div>
                  ) : null}

                  {getEditKeyForTemplateId(selectedCommunityId) ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 900 }}>Owner versions</div>
                      {ownerVersionsError ? (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#ffb4b4" }}>{ownerVersionsError}</div>
                      ) : null}
                      {ownerVersions.length === 0 ? (
                        <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                          No versions loaded yet. Click â€œVersionsâ€ above.
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                          {ownerVersions.slice(0, 10).map((v) => (
                            <div key={`v_${v.idx}_${v.ts}`} style={{ padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                                {v.ts ? new Date(v.ts).toLocaleString() : ""} {v.name ? ` â€¢ ${String(v.name)}` : ""}
                              </div>
                              {v.description ? (
                                <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
                                  {String(v.description)}
                                </div>
                              ) : null}
                              <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="generate-btn" type="button" onClick={() => restoreOwnerVersion(selectedCommunityId, v.idx)}>
                                  Restore
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {selectedCommunityTemplate.description ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.78)" }}>
                      {String(selectedCommunityTemplate.description)}
                    </div>
                  ) : null}
                  {Array.isArray(selectedCommunityTemplate.tags) && selectedCommunityTemplate.tags.length > 0 ? (
                    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {selectedCommunityTemplate.tags.slice(0, 12).map((t) => (
                        <span key={`sel_tag_${t}`} style={{ padding: "4px 8px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)" }}>
                          #{t}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <div style={{ marginTop: 10, fontWeight: 900 }}>Comments</div>
                  <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      value={newCommentText}
                      onChange={(e) => setNewCommentText(e.target.value)}
                      placeholder="Write a comment..."
                      style={{
                        flex: "1 1 280px",
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(0,0,0,0.18)",
                        color: "#ffffff",
                        outline: "none",
                      }}
                    />
                    <button className="generate-btn" type="button" disabled={!newCommentText.trim()} onClick={addCommentToSelected}>
                      Post
                    </button>
                  </div>
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                    {Array.isArray(selectedCommunityTemplate.comments) && selectedCommunityTemplate.comments.length > 0 ? (
                      selectedCommunityTemplate.comments.slice(0, 8).map((c, idx) => (
                        <div key={`c_${idx}_${c.ts || idx}`} style={{ padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                            {c.ts ? new Date(c.ts).toLocaleString() : ""}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{String(c.text || "")}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>No comments yet.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {filteredCommunity.length === 0 ? (
            <div style={{ marginTop: 10, color: "rgba(255,255,255,0.75)" }}>No community templates yet.</div>
          ) : (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredCommunity.slice(0, 10).map((c) => (
                <div key={c.id} style={cardStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span>{c.name || "Template"}</span>
                      {getEditKeyForTemplateId(c.id) ? (
                        <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, border: "1px solid rgba(34,197,94,0.45)", color: "rgba(34,197,94,0.95)", background: "rgba(34,197,94,0.12)" }}>
                          Owned
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                      {c.id} â€¢ {c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}{c.author ? ` â€¢ by ${String(c.author)}` : ""}
                      {c.sourceId ? (
                        <>
                          {" "}
                          â€¢ remix of{" "}
                          <button
                            type="button"
                            className="generate-btn"
                            style={{ padding: "2px 8px", fontSize: 12 }}
                            onClick={() => goToTemplateId(c.sourceId)}
                          >
                            {String(c.source?.name || sourceMetaById?.[String(c.sourceId)]?.name || c.sourceId)}
                          </button>
                        </>
                      ) : null}
                      {" "}
                      {c.updatedAt ? ` â€¢ Updated ${new Date(Number(c.updatedAt)).toLocaleDateString()}` : ""}
                      {communitySort === "hot" && c.hotScore != null ? ` â€¢ ðŸ”¥ ${Number(c.hotScore || 0)}` : ""}
                      â€¢ ðŸ‘ï¸ {Number(c.viewCount || 0)} â€¢ â¤ï¸ {Number(c.likes || 0)} â€¢ ðŸ” {Number(c.remixes || 0)} â€¢ ðŸ’¬ {Number(c.commentCount || 0)}
                    </div>
                    {(c.description || (Array.isArray(c.tags) && c.tags.length > 0)) && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                        {c.description ? <div style={{ marginBottom: 4 }}>{String(c.description)}</div> : null}
                        {Array.isArray(c.tags) && c.tags.length > 0 ? (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {c.tags.slice(0, 8).map((t) => (
                              <span key={`${c.id}_tag_${t}`} style={{ padding: "4px 8px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)" }}>
                                #{t}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="generate-btn" type="button" onClick={() => { setSelectedCommunityId(c.id); setViewInUrl(c.id); loadCommunityTemplate(c.id); }}>
                      View
                    </button>
                    <button className="generate-btn" type="button" onClick={() => openCommunityTemplateInBuilder(c.id)}>
                      Open
                    </button>
                    <button className="generate-btn" type="button" onClick={() => saveCommunityTemplateToLocal(c.id)}>
                      Save
                    </button>
                    <button className="generate-btn" type="button" onClick={() => downloadCommunityTemplateJson(c.id)}>
                      JSON
                    </button>
                    <button className="generate-btn" type="button" onClick={() => copyTemplateShareLink(c.id)}>
                      Copy link
                    </button>
                    <button className="generate-btn" type="button" onClick={() => { setShareId(c.id); importFromShareId(); }}>
                      Import
                    </button>
                    <button className="generate-btn" type="button" onClick={() => likeCommunity(c.id)}>
                      Like
                    </button>
                    <button className="generate-btn" type="button" onClick={() => remixCommunity(c.id)}>
                      Remix
                    </button>
                    <button className="generate-btn" type="button" onClick={() => commentCommunity(c.id)}>
                      Comment
                    </button>
                    <button className="cancel-btn" type="button" onClick={() => reportCommunity(c.id)}>
                      Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={sectionStyle} aria-labelledby="saved-heading">
          <h2 id="saved-heading">Saved templates</h2>
          {sorted.length === 0 ? (
            <div style={{ color: "rgba(255,255,255,0.75)" }}>No templates yet. Save one from the WebsiteBuilder.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sorted.map((tpl) => (
                <div key={tpl.id} style={cardStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900 }}>{tpl.name || "Template"}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                      {tpl.createdAt ? new Date(tpl.createdAt).toLocaleString() : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="generate-btn" type="button" onClick={() => doLoadIntoBuilder(tpl)}>
                      Load in builder
                    </button>
                    <button className="generate-btn" type="button" onClick={() => publishTemplate(tpl)}>
                      Publish
                    </button>
                    <button
                      className="generate-btn"
                      type="button"
                      onClick={() => downloadJson(`zolaid-template-${tpl.id}.json`, tpl)}
                    >
                      Export
                    </button>
                    <button
                      className="cancel-btn"
                      type="button"
                      onClick={() => setTemplates((prev) => prev.filter((x) => x.id !== tpl.id))}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <p style={{ marginTop: 14, color: "rgba(255,255,255,0.75)" }}>
          Local templates are stored in your browser (<code>localStorage</code>). Use Export to back them up.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

const sectionStyle = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.10)",
  background: "rgba(255,255,255,0.04)",
  marginBottom: 12,
};

const textareaStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(0,0,0,0.18)",
  color: "#ffffff",
  outline: "none",
};

const cardStyle = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.10)",
  background: "rgba(255,255,255,0.03)",
};

